import React, { useState, useEffect } from "react";
import { useNavigate, useLocation, Link } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import toast from "react-hot-toast";
import { Formik, Form } from "formik";
import * as Yup from "yup";
import AuthLayout from "../components/layout/AuthLayout";
import { OTPInput, Button } from "../components/ui";

const VerifyEmailSchema = Yup.object().shape({
  otp: Yup.string()
    .required("OTP is required")
    .min(6, "OTP must be at least 6 characters")
    .max(6, "OTP must be at most 6 characters"),
});

const VerifyEmail = () => {
  const [resendTimer, setResendTimer] = useState(0);
  const [canResend, setCanResend] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const { verifyEmail, resendSignupOTP } = useAuth();
  const email = location.state?.email;

  useEffect(() => {
    let interval = null;
    if (resendTimer > 0) {
      interval = setInterval(() => {
        setResendTimer((prev) => {
          if (prev <= 1) {
            setCanResend(true);
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    }
    return () => clearInterval(interval);
  }, [resendTimer]);

  useEffect(() => {
    if (email) {
      setResendTimer(60);
      setCanResend(false);
    }
  }, [email]);

  const handleResendOTP = async () => {
    try {
      await resendSignupOTP(email);
      setResendTimer(60);
      setCanResend(false);
      toast.success("OTP resent to your email");
    } catch (error) {
      toast.error(error.message || "Failed to resend OTP");
    }
  };

  if (!email) {
    navigate("/signup");
    return null;
  }

  const handleSubmit = async (values, { setSubmitting }) => {
    try {
      await verifyEmail(email, values.otp);
      toast.success("Email verified successfully");
      navigate("/login");
    } catch (error) {
      toast.error(error.message || "Failed to verify email");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <AuthLayout>
      <div className="space-y-8">
        <div>
          <h2 className="font-heading text-4xl font-bold text-gray-900">
            Verify your email
          </h2>
          <p className="text-sm text-gray-500 mt-3">
            We've sent a verification code to {email}
          </p>
        </div>

        <Formik
          initialValues={{ otp: "" }}
          validationSchema={VerifyEmailSchema}
          onSubmit={handleSubmit}
        >
          {({
            errors,
            touched,
            isSubmitting,
            values,
            setFieldValue,
            handleBlur,
          }) => (
            <Form className="space-y-5">
              <OTPInput
                name="otp"
                value={values.otp}
                onChange={(e) => setFieldValue("otp", e.target.value)}
                onBlur={handleBlur}
                error={errors.otp && touched.otp ? errors.otp : undefined}
                resendTimer={resendTimer}
                onResend={handleResendOTP}
              />

              <Button
                type="submit"
                fullWidth
                loading={isSubmitting}
                disabled={isSubmitting}
                className="py-3"
              >
                Verify email
              </Button>

              <p className="text-center text-sm text-gray-500">
                <Link
                  to="/login"
                  className="font-medium text-primary hover:text-primary-dark"
                >
                  Back to login
                </Link>
              </p>
            </Form>
          )}
        </Formik>
      </div>
    </AuthLayout>
  );
};

export default VerifyEmail;
